import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Search, ArrowUpRight, Unlock } from 'lucide-react';
import { useLibrary } from '../../lib/library';
import { Reveal, Eyebrow } from '../../components/Reveal';
import { PageHead } from '../../components/u99/PageHead';

const Row = ({ r }) => (
  <Link to={r.route} className="group grid grid-cols-[6.5rem_1fr_auto] sm:grid-cols-[7rem_1fr_10rem_auto] items-center gap-3 sm:gap-6 py-3.5 border-t border-white/[0.06] hover:bg-amber-400/[0.03] transition-colors" data-testid={`library-row-${r.id}`}>
    <span className="font-mono-precision text-[9.5px] uppercase tracking-[0.22em] text-slate-500">{r.kind}{r.number != null ? ` · ${String(r.number).padStart(2, '0')}` : ''}</span>
    <span className="font-celestial text-sm sm:text-base text-slate-200 group-hover:text-amber-100 transition-colors leading-snug">{r.title}</span>
    <span className={`hidden sm:block font-mono-precision text-[9px] uppercase tracking-[0.2em] ${r.words > 0 ? 'text-emerald-400/80' : 'text-slate-600'}`} data-testid={`library-status-${r.id}`}>{r.words > 0 ? `Full text · ${r.words.toLocaleString()} words` : 'Title page · text pending'}</span>
    <span className="font-mono-precision text-[9px] text-slate-600 group-hover:text-amber-300 inline-flex items-center gap-1 transition-colors">{r.route} <ArrowUpRight size={11} /></span>
  </Link>
);

export default function Library() {
  const lib = useLibrary();
  const [q, setQ] = useState('');
  useEffect(() => { document.title = 'Library — every publication — United 99 Unleashed'; }, []);
  const all = lib?.rows.filter((r) => r.live) || [];
  const needle = q.trim().toLowerCase();
  const shown = needle ? all.filter((r) => `${r.kind} ${r.title} ${r.route}`.toLowerCase().includes(needle)) : all;
  const withText = all.filter((r) => r.words > 0 && r.kind !== 'Chapter' && r.kind !== 'Book').length;
  const works = all.filter((r) => r.kind !== 'Chapter').length;

  return (
    <main className="max-w-7xl mx-auto px-5 lg:px-10" data-testid="library-page">
      <PageHead eyebrow="Library · Open access · No sign-up, no paywall" title={<>Every work, <span className="text-amber-300">open to all</span></>} lede="The complete public inventory of United 99 Unleashed: every doctrine, publication, thesis, manuscript, novel and book chapter, with its own address. Nothing here is gated. Nothing asks for an email. Click any title to read it now." />
      <section className="py-8 lg:py-12 border-t border-white/[0.07]">
        <Reveal>
          <div className="flex flex-wrap items-center gap-x-8 gap-y-3 font-mono-precision text-[10px] uppercase tracking-[0.2em] text-slate-500" data-testid="library-counts">
            <span className="inline-flex items-center gap-2 text-emerald-400"><Unlock size={12} /> 100% free · open access</span>
            <span>{works} works listed</span>
            <span>{withText} with full text live</span>
            <span>{all.filter((r) => r.kind === 'Chapter').length} book chapters</span>
          </div>
          <label className="mt-8 flex items-center gap-3 rounded-full border border-white/[0.1] focus-within:border-amber-400/50 px-5 py-3 max-w-xl transition-colors">
            <Search size={14} className="text-slate-500" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search every publication by title, kind or route…" className="flex-1 bg-transparent outline-none text-sm text-slate-200 placeholder:text-slate-600" data-testid="library-search-input" />
          </label>
        </Reveal>
      </section>
      <section className="pb-24" data-testid="library-list">
        <Reveal><Eyebrow>{needle ? `${shown.length} matching` : 'Full inventory'}</Eyebrow></Reveal>
        {!lib && <p className="py-10 font-mono-precision text-xs text-slate-600 tracking-[0.2em]">LOADING…</p>}
        {shown.map((r) => <Row key={r.id} r={r} />)}
        {lib && shown.length === 0 && <p className="py-10 font-mono-precision text-xs text-slate-600 tracking-[0.2em]" data-testid="library-empty">NOTHING MATCHES</p>}
      </section>
    </main>
  );
}
